/* eslint-env node */
/**
 * Shared heuristic for "is this English bleed in FR/ES?"
 *
 * Used by:
 *   - frontend/src/lib/i18nStrict.js (dev-time runtime guard)
 *   - frontend/tests/i18n_audit.spec.js (Playwright regression test)
 *
 * Approach:
 *   We do NOT try to detect English. We try to detect *non-English*
 *   signals. A phrase is considered "English bleed" only when:
 *     1. It has ≥4 multi-letter words
 *     2. Zero French OR Spanish indicator words appear
 *     3. Zero accented characters appear
 *     4. ≥3 of the words match a high-frequency English dictionary
 *
 * Tuning rationale: we'd rather under-flag than fire false positives;
 * over-flagging trains operators to ignore the warnings.
 */

// French indicator tokens (very common short words that almost never
// appear in English copy).
const FR_HINTS = new Set([
    'le', 'la', 'les', 'un', 'une', 'des', 'du', 'de', 'au', 'aux',
    'ce', 'cette', 'ces', 'cet',
    'et', 'ou', 'mais', 'donc', 'car', 'si',
    'votre', 'vos', 'notre', 'nos', 'mon', 'ma', 'mes', 'ton', 'ta', 'tes',
    'leur', 'leurs', 'son', 'sa', 'ses',
    'qui', 'que', 'quoi', 'dont', 'où',
    'avec', 'sans', 'pour', 'sur', 'sous', 'dans', 'chez', 'entre', 'vers',
    'plus', 'moins', 'très', 'tout', 'tous', 'toute', 'toutes',
    'aucun', 'aucune', 'tous',
    'est', 'sont', 'était', 'étaient', 'sera', 'seront', 'soit',
    'avoir', 'être', 'faire', 'aller', 'voir', 'savoir', 'pouvoir', 'devoir',
    'cliquez', 'sélectionnez', 'choisissez', 'utilisez',
    'merci', 'bonjour', 'bienvenue', 'aujourd',
    'cadence', 'avis', 'bons', 'bon', 'emplacement', 'emplacements',
    'sondage', 'sondages', 'statut', 'paramètres', 'paramètre',
    'tableau', 'bord', 'aide', 'sortie', 'retour', 'suivi', 'gestion',
    'manuel', 'exploitation', 'utilisateurs', 'utilisateur',
    'registre', 'bouclier', 'cartes', 'carte', 'cadeaux',
    'aperçu', 'analyse', 'analyses', 'analytiques', 'rapports', 'rapport',
    'organisations', 'organisation', 'marques', 'marque',
    'ventes', 'vente', 'modifier', 'voir', 'créer', 'enregistrer',
    'précédent', 'suivant', 'fermer', 'annuler', 'continuer',
    'opérationnelle', 'opérations', 'flotte',
]);

// Spanish indicator tokens.
const ES_HINTS = new Set([
    'el', 'la', 'los', 'las', 'un', 'una', 'unos', 'unas', 'del',
    'y', 'o', 'pero', 'porque', 'si',
    'su', 'sus', 'mi', 'mis', 'tu', 'tus', 'nuestro', 'nuestra',
    'que', 'cual', 'quien', 'donde',
    'con', 'sin', 'para', 'por', 'sobre', 'bajo', 'entre', 'hacia',
    'más', 'menos', 'muy', 'todo', 'toda', 'todos', 'todas',
    'es', 'son', 'fue', 'fueron', 'será', 'serán', 'ser', 'estar',
    'tener', 'hacer', 'ir', 'ver', 'saber', 'poder', 'deber',
    'haga', 'clic', 'seleccione', 'elija', 'use',
    'gracias', 'bienvenido', 'bienvenida', 'hoy',
    'velocidad', 'reseña', 'reseñas', 'vales', 'vale', 'ubicación', 'ubicaciones',
    'encuesta', 'encuestas', 'estado', 'configuración',
    'panel', 'tablero', 'ayuda', 'salida', 'regreso', 'seguimiento', 'gestión',
    'manual', 'operación', 'operativo', 'usuarios', 'usuario',
    'registro', 'escudo', 'tarjetas', 'tarjeta', 'regalo',
    'resumen', 'análisis', 'analítica', 'informes', 'informe',
    'organizaciones', 'organización', 'marcas', 'marca',
    'ventas', 'venta', 'modificar', 'ver', 'crear', 'guardar',
    'anterior', 'siguiente', 'cerrar', 'cancelar', 'continuar',
    'operativa', 'operaciones', 'flota', 'empleado', 'empleados',
]);

// High-frequency English-only function words / verbs / nouns. If we see
// ≥3 of these in a phrase with no FR/ES hints, that's bleed.
const EN_HINTS = new Set([
    'the', 'and', 'or', 'but', 'because', 'if', 'when', 'where', 'while',
    'with', 'without', 'for', 'from', 'to', 'into', 'onto', 'over', 'under',
    'this', 'that', 'these', 'those',
    'your', 'our', 'their', 'his', 'her', 'its',
    'who', 'whom', 'which', 'what', 'why', 'how',
    'is', 'are', 'was', 'were', 'be', 'been', 'being', 'have', 'has', 'had',
    'do', 'does', 'did', 'can', 'could', 'should', 'would', 'will', 'shall',
    'click', 'select', 'choose', 'enter', 'type', 'set', 'save', 'cancel',
    'submit', 'export', 'import', 'refresh', 'view', 'edit', 'delete',
    'welcome', 'thanks', 'thank', 'hello', 'today',
    'all', 'any', 'some', 'every', 'each', 'no', 'none',
    'new', 'old', 'first', 'last', 'next', 'previous', 'back',
    'showing', 'shown', 'showed', 'show',
    'velocity', 'reviews', 'vouchers', 'voucher', 'gift', 'cards', 'card',
    'location', 'locations', 'employee', 'employees', 'survey', 'surveys',
    'status', 'settings', 'dashboard', 'help', 'exit', 'overview',
    'operations', 'manual', 'users', 'user', 'registry', 'shield',
    'organization', 'organizations', 'brand', 'brands', 'sales',
    'platforms', 'platform', 'statuses',
    'tags', 'tag', 'landing', 'page',
]);

const ACCENT_RE = /[\u00C0-\u017F\u0180-\u024F]/;
const STRIP_RE = /[^\p{L}\s'-]/gu;

/**
 * Returns the longest English-bleed run found in `text`, or null.
 */
function detectEnglishBleed(text) {
    if (!text || text.length < 20) return null;

    // Phrases shorter than ~5 words are too ambiguous; skip.
    const cleaned = text.replace(STRIP_RE, ' ').trim();
    if (!cleaned) return null;
    const words = cleaned.split(/\s+/).filter((w) => w.length > 1);
    if (words.length < 5) return null;

    // If ANY word has an accent → it's not English. Stop.
    for (const w of words) {
        if (ACCENT_RE.test(w)) return null;
    }

    // Count language hints.
    let frHits = 0;
    let esHits = 0;
    let enHits = 0;
    for (const w of words) {
        const lw = w.toLowerCase();
        if (FR_HINTS.has(lw)) frHits += 1;
        if (ES_HINTS.has(lw)) esHits += 1;
        if (EN_HINTS.has(lw)) enHits += 1;
    }

    // If we see ANY French or Spanish hint, this is not English bleed.
    if (frHits > 0 || esHits > 0) return null;

    // Need at least 3 distinct English markers.
    if (enHits < 3) return null;

    // Return up to 12 words of context.
    return words.slice(0, 12).join(' ');
}

module.exports = { detectEnglishBleed, FR_HINTS, ES_HINTS, EN_HINTS };
